import { useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeAdminFacingMessage } from '../errors/toAdminFacingErrorMessage.js';
import styles from './FormErrorSummary.module.css';

export interface FormErrorSummaryItem {
  readonly id: string;
  readonly message: string;
}

export interface FormErrorSummaryProps {
  readonly title?: string;
  readonly errors: ReadonlyArray<FormErrorSummaryItem>;
  /** Focus summary on mount via ref (default true). Not the JSX autoFocus attribute. */
  readonly focusOnMount?: boolean;
  /**
   * Optional navigate hook for multi-tab forms (switch tab then focus).
   * When omitted, links focus `document.getElementById(id)` directly.
   */
  readonly onNavigateToError?: (fieldId: string) => void;
}

/**
 * GOV.UK-style error summary — links each message to its field id.
 * Mount at top of form on submit failure; focus summary for screen readers.
 */
export function FormErrorSummary({
  title,
  errors,
  focusOnMount = true,
  onNavigateToError,
}: FormErrorSummaryProps): ReactElement | null {
  const { t } = useTranslation('admin');
  const summaryRef = useRef<HTMLDivElement>(null);
  const visibleErrors = errors.filter((item) => item.message.trim().length > 0);

  useEffect(() => {
    if (!focusOnMount || visibleErrors.length === 0) {
      return;
    }
    summaryRef.current?.focus();
  }, [focusOnMount, visibleErrors.length]);

  if (visibleErrors.length === 0) {
    return null;
  }

  const heading = title ?? t('validation.formErrorSummaryTitle');

  return (
    <div
      ref={summaryRef}
      className={styles.root}
      role="alert"
      tabIndex={-1}
      data-testid="form-error-summary"
      aria-labelledby="form-error-summary-title"
    >
      <h2 id="form-error-summary-title" className={styles.title}>
        {heading}
      </h2>
      <ul className={styles.list}>
        {visibleErrors.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className={styles.link}
              onClick={(event) => {
                event.preventDefault();
                if (onNavigateToError != null) {
                  onNavigateToError(item.id);
                  return;
                }
                const target = document.getElementById(item.id);
                if (target instanceof HTMLElement) {
                  target.focus();
                  target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
              }}
            >
              {sanitizeAdminFacingMessage(item.message)}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
